import type React from 'react';
import { type FC, useRef, useState } from 'react';

import type { CancellationReason } from '../../../lib/cancellation-api';
import { compressForUpload } from '../../../lib/image-compress';
import { formatBytes } from '../../utils/helper';
import { VideoEvidenceField } from '../../utils/video-evidence';
import { type IVoiceNote } from '../../utils/voice-note/validate';
import VoiceNoteRecorder from '../../utils/voice-note/VoiceNoteRecorder';
import { CANCELLATION_REASONS, reasonLabel } from './reasons';
import { CameraGlyph } from './shared';

export const MAX_NOTE = 1000;
/** Server cap for an image (contract: evidence/sign). Photos are compressed first, so this is rare. */
export const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

export interface IPhoto {
	id: string;
	file: File;
	url: string;
}

// ── Step 1: reason ──

export const ReasonStep: FC<{ value: CancellationReason | null; onPick: (r: CancellationReason) => void }> = ({
	value,
	onPick,
}) => (
	<section
		className='pc-step pc-step--reason'
		aria-labelledby='pc-reason-title'
	>
		<h4
			id='pc-reason-title'
			className='pc-step__title'
		>
			Why did INEC cancel it?
		</h4>
		<div
			className='pc-reasons'
			role='group'
			aria-labelledby='pc-reason-title'
		>
			{CANCELLATION_REASONS.map((r) => (
				<button
					key={r.value}
					type='button'
					aria-pressed={value === r.value}
					className={`pc-reason${value === r.value ? ' is-selected' : ''}`}
					onClick={() => onPick(r.value)}
				>
					{r.label}
				</button>
			))}
		</div>
	</section>
);

// ── Step 2: evidence (optional) ──

interface IEvidenceStepProps {
	photos: IPhoto[];
	setPhotos: (next: IPhoto[]) => void;
	video: File | null;
	setVideo: (f: File | null) => void;
	voice: IVoiceNote | null;
	setVoice: (v: IVoiceNote | null) => void;
	note: string;
	setNote: (s: string) => void;
	/** False when the server can't take files (v1 ledger): only the note is offered. */
	filesSupported: boolean;
	showNote: boolean;
}

export const EvidenceStep: FC<IEvidenceStepProps> = ({
	photos,
	setPhotos,
	video,
	setVideo,
	voice,
	setVoice,
	note,
	setNote,
	filesSupported,
	showNote,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [photoError, setPhotoError] = useState('');
	const [busy, setBusy] = useState(false);

	const onPhotos = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
		const picked = Array.from(target.files ?? []);
		target.value = '';
		if (!picked.length) return;
		setBusy(true);
		setPhotoError('');
		const next: IPhoto[] = [];
		for (const raw of picked) {
			if (!raw.type.startsWith('image/')) continue;
			const file = await compressForUpload(raw);
			if (file.size > MAX_IMAGE_BYTES) {
				setPhotoError(`Each photo must be ${formatBytes(MAX_IMAGE_BYTES)} or less.`);
				continue;
			}
			next.push({ id: crypto.randomUUID(), file, url: URL.createObjectURL(file) });
		}
		setPhotos([...photos, ...next]);
		setBusy(false);
	};

	const remove = (id: string) => {
		const hit = photos.find((p) => p.id === id);
		if (hit) URL.revokeObjectURL(hit.url);
		setPhotos(photos.filter((p) => p.id !== id));
	};

	return (
		<section
			className='pc-step'
			aria-labelledby='pc-evidence-title'
		>
			<h4
				id='pc-evidence-title'
				className='pc-step__title'
			>
				Add evidence <span className='pc-step__optional'>optional</span>
			</h4>

			{filesSupported ? (
				<>
					<div className='pc-ev-block'>
						<input
							ref={inputRef}
							type='file'
							accept='image/*'
							capture='environment'
							multiple
							className='d-none'
							onChange={onPhotos}
							data-testid='pc-photo-input'
						/>
						<button
							type='button'
							className='pc-capture'
							onClick={() => inputRef.current?.click()}
							disabled={busy}
						>
							<CameraGlyph />
							<span>{busy ? 'Preparing photo…' : photos.length ? 'Take another photo' : 'Take a photo'}</span>
						</button>
						{photos.length ? (
							<ul className='pc-thumbs'>
								{photos.map((p, i) => (
									<li
										key={p.id}
										className='pc-thumb'
									>
										<img
											src={p.url}
											alt={`Evidence ${i + 1}`}
										/>
										<button
											type='button'
											className='pc-thumb__remove'
											onClick={() => remove(p.id)}
											aria-label={`Remove photo ${i + 1}`}
										>
											×
										</button>
									</li>
								))}
							</ul>
						) : null}
						{photoError ? <p className='pc-field-error'>{photoError}</p> : null}
					</div>

					<div className='pc-ev-block'>
						<VoiceNoteRecorder
							value={voice}
							onChange={setVoice}
						/>
					</div>

					<div className='pc-ev-block'>
						<VideoEvidenceField
							video={video}
							setVideo={setVideo}
						/>
					</div>
				</>
			) : (
				<p className='pc-step__hint'>Your party can’t receive photos or voice notes for this election yet. Add a note instead.</p>
			)}

			{showNote ? (
				<label className='pc-note'>
					<span className='pc-note__label'>Note</span>
					<input
						type='text'
						className='pc-note__input'
						value={note}
						maxLength={MAX_NOTE}
						placeholder='What happened, in a few words'
						onChange={(e) => setNote(e.target.value)}
					/>
				</label>
			) : null}
		</section>
	);
};

// ── Step 3: confirm ──

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const evidenceSummary = (photos: number, voice: boolean, video: boolean): string => {
	const parts = [photos ? plural(photos, 'photo', 'photos') : '', voice ? '1 voice note' : '', video ? '1 video' : ''].filter(
		Boolean
	);
	return parts.length ? parts.join(', ') : 'No evidence';
};

interface IConfirmStepProps {
	pollingUnitCode: string;
	pollingUnitName?: string;
	reason: CancellationReason;
	summary: string;
	note: string;
	location: 'pending' | 'ok' | 'none';
}

export const ConfirmStep: FC<IConfirmStepProps> = ({ pollingUnitCode, pollingUnitName, reason, summary, note, location }) => (
	<section
		className='pc-step'
		aria-labelledby='pc-confirm-title'
	>
		<h4
			id='pc-confirm-title'
			className='pc-step__title'
		>
			Check and send
		</h4>
		<div className='pc-recap'>
			<p className='pc-recap__line'>
				INEC cancelled <strong>PU {pollingUnitCode}</strong>
				{pollingUnitName ? ` · ${pollingUnitName}` : ''}
			</p>
			<div className='pc-recap__chips'>
				<span className='pc-recap__reason'>{reasonLabel(reason)}</span>
				<span className='pc-recap__count'>{summary}</span>
			</div>
			{note.trim() ? <p className='pc-recap__note'>“{note.trim()}”</p> : null}
		</div>
		<p className='pc-step__hint'>
			{location === 'ok'
				? 'Your location is attached.'
				: location === 'pending'
					? 'Getting your location — the report won’t wait for it.'
					: 'No location available. The report sends without it.'}
		</p>
	</section>
);
